import { Link } from "react-router";

import { CounterpartyCheck } from "../counterparty/CounterpartyCheck.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { WorkThumb } from "../lib/WorkThumb.tsx";
import { OFFERS_COPY } from "./transfers.constants.ts";
import { useTransferOffersController } from "./useTransferOffersController.ts";
import type { OfferRow } from "./transferOffers.model.ts";
import "./transfers.css";

/**
 * Title somebody has offered you, and the two answers to it.
 *
 * The buyer's side of the gallery's Sold Works row, and the screen that makes an
 * on-ONDO sale finishable. `ondo.effect_sale()` records a sale and OFFERS title
 * — it cannot move it, because the buyer has consented to nothing when the
 * gallery presses Convert. Until this existed the sale parked at "awaiting
 * buyer" with nothing anywhere able to settle it.
 *
 * IT DISCLOSES THE COVENANT AS LOUDLY AS THE PRICE. The gallery set those terms
 * as sliders; the buyer has never seen them, cannot change them, and — when the
 * covenant locks — cannot sell the work on at all. A screen showing what it
 * costs and hiding what it binds would be the wrong half.
 */
export function TransferOffers({
  workBase,
  selfOndoId,
}: {
  workBase: string;
  /**
   * The reader's own ONDO id, so a purchase made FOR A FIRM can be told from
   * one made for them — OND-184. Passed in rather than read here: the workspace
   * already holds `MeResponse.principal` and a second read of `/me` on this
   * screen would be a second answer to a question already answered.
   */
  selfOndoId: string;
}) {
  const { view, chosen, choose, acknowledged, acknowledge, accept, decline, busy } =
    useTransferOffersController(workBase, selfOndoId);

  if (view.kind === "loading") {
    return (
      <p className="offers__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="offers__note offers__note--error" role="alert">
        <h2 className="offers__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="offers__empty" lead={view.message} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="offers__summary">{view.summary}</p>

      <section className="offers__lead">
        <p>
          <strong>{OFFERS_COPY.lead}</strong> {OFFERS_COPY.leadAction}
        </p>
      </section>

      <ul className="offers__rows">
        {view.rows.map((row) => (
          <OfferCard
            key={row.transferId}
            row={row}
            chosen={chosen.get(row.transferId) ?? ""}
            onChoose={(collectionId) => choose(row.transferId, collectionId)}
            acknowledged={acknowledged.has(row.transferId)}
            onAcknowledge={(next) => acknowledge(row.transferId, next)}
            onAccept={() => accept(row.transferId, row.asOndoId)}
            onDecline={() => decline(row.transferId)}
            busy={busy?.transferId === row.transferId ? busy.accepting : null}
            anyBusy={busy !== null}
          />
        ))}
      </ul>
    </>
  );
}

function OfferCard({
  row,
  chosen,
  onChoose,
  onAccept,
  onDecline,
  acknowledged,
  onAcknowledge,
  busy,
  anyBusy,
}: {
  row: OfferRow;
  chosen: string;
  onChoose: (collectionId: string) => void;
  /** OND-85. Whether this row's inherited obligations have been acknowledged. */
  acknowledged: boolean;
  onAcknowledge: (next: boolean) => void;
  onAccept: () => void;
  onDecline: () => void;
  /** true accepting, false declining, null when this row is idle. */
  busy: boolean | null;
  anyBusy: boolean;
}) {
  return (
    <li className="offers__row">
      {/* The record, readable only while the offer is open — accepting or
          declining closes `ondo.transfer_offered_work_ids()` in the same
          statement, so this link is the buyer's one window onto the work. */}
      <Link className="offers__row-open" to={row.href}>
        <WorkThumb imageUrl={row.imageUrl} title={row.title} />
        <div className="offers__row-body">
          <h3 className="offers__row-title">{row.title}</h3>
          <p className="offers__row-identity">{row.identity}</p>
          <p className="offers__row-code">{row.ondoCode}</p>
        </div>
      </Link>

      <dl className="offers__facts">
        <div>
          <dt>{OFFERS_COPY.price}</dt>
          <dd className="offers__price">{row.price}</dd>
        </div>
        <div>
          <dt>{OFFERS_COPY.seller}</dt>
          <dd>
            {row.seller}
            {/* OND-81: a buyer may check who is offering them title. */}
            <CounterpartyCheck ondoId={row.sellerOndoId} />
          </dd>
        </div>
        <div>
          <dt>{OFFERS_COPY.gallery}</dt>
          <dd>{row.through}</dd>
        </div>
      </dl>

      <section className={`offers__covenant offers__covenant--${row.covenant.kind}`}>
        <h4 className="offers__covenant-legend">{OFFERS_COPY.covenantLegend}</h4>
        <p className="offers__covenant-summary">{row.covenant.summary}</p>
        {row.covenant.kind !== "none" && (
          <p className="offers__covenant-lock">{row.covenant.lock}</p>
        )}
      </section>

      {/* OND-85. A SECTION OF ITS OWN, beside the covenant above and never
          merged with it: that one is what this buyer is being asked to GRANT,
          and this is what they would ASSUME from an earlier sale. They were the
          same three columns until OND-85, which is how a gallery came to read
          "yours to sell on freely" about a picture that owed money. */}
      <section className={`offers__inherited offers__inherited--${row.inherited.kind}`}>
        <h4 className="offers__inherited-legend">{OFFERS_COPY.inheritedLegend}</h4>
        <p className="offers__inherited-summary">{row.inherited.summary}</p>
        {row.inherited.kind !== "none" && (
          <>
            <p className="offers__inherited-cost">{row.inherited.cost}</p>
            <p className="offers__inherited-lock">{row.inherited.lock}</p>
            <p className="offers__inherited-basis">{row.inherited.basis}</p>
            <Link className="offers__inherited-detail" to={row.inherited.detailHref}>
              {row.inherited.detail}
            </Link>
          </>
        )}
      </section>

      <div className="offers__actions">
        {/* OND-184. Drawn only when the buyer is not the reader, because that is
            the only case worth a sentence — and it is a STATEMENT rather than a
            choice: `ondo.transfers.to_principal_id` was written when the sale
            was and is frozen, so there is nothing here for the reader to pick.
            What there is, is the thing they were never told. */}
        {row.capacity && (
          <p className="offers__capacity" role="status">
            <strong>{row.capacity.lead}</strong> {row.capacity.party}
          </p>
        )}

        <label className="offers__field">
          <span className="offers__label">{OFFERS_COPY.collectionLabel}</span>
          <select
            className="offers__select"
            value={chosen}
            disabled={row.collections.length === 0 || anyBusy}
            onChange={(event) => onChoose(event.currentTarget.value)}
          >
            <option value="">{OFFERS_COPY.collectionPlaceholder}</option>
            {row.collections.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </select>
        </label>

        {/* The one state this screen cannot resolve for the reader: title has to
            land somewhere the BUYING PARTY owns, and they own nowhere. Per row
            since OND-184, because the party differs per row and a director with
            three collections of their own still has nowhere to put the firm's
            purchase. */}
        {row.noCollections && (
          <p className="offers__note offers__note--warn" role="status">
            {row.noCollections}
          </p>
        )}

        {/* OND-85. Rendered only when something actually binds, because a
            consent control on a work that owes nothing trains the reader to
            tick it. `ondo.answer_transfer()` refuses the acceptance without it
            and freezes what was shown when it has it, so this is the client
            half of a rule the server keeps — not the rule itself. */}
        {row.inherited.kind !== "none" && (
          <label className="offers__acknowledge">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={anyBusy}
              onChange={(event) => onAcknowledge(event.currentTarget.checked)}
            />
            <span>{OFFERS_COPY.acknowledge}</span>
          </label>
        )}

        <div className="offers__buttons">
          {/* Held until a collection is chosen, which is the client half of
              `answerTransferSchema`'s refine — the server cannot pick one on the
              buyer's behalf, so "you did not say where" is a question about the
              request rather than a refusal about the row.
              AND until the obligations are acknowledged, which is OND-85's: the
              screen must not offer a button the database would refuse. */}
          <button
            type="button"
            className="offers__accept"
            disabled={
              chosen === "" ||
              anyBusy ||
              (row.inherited.kind !== "none" && !acknowledged)
            }
            onClick={onAccept}
          >
            {busy === true ? OFFERS_COPY.accepting : OFFERS_COPY.accept}
          </button>
          <button
            type="button"
            className="offers__decline"
            disabled={anyBusy}
            onClick={onDecline}
          >
            {busy === false ? OFFERS_COPY.declining : OFFERS_COPY.decline}
          </button>
        </div>

        <p className="offers__decline-note">{OFFERS_COPY.declineNote}</p>
      </div>
    </li>
  );
}
